import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { connect } from 'react-redux';

import InspectionsServices from '../../../services/InspectionsServices';
import CommentBox from '../molecules/CommentBox';
import DateBox from '../molecules/DateBox';
import SelectBox from '../molecules/SelectBox';
import PhotoBox from '../molecules/PhotoBox';
import ModalBody from '../../shared/molecules/ModalBody';

const filterCheckpoints = (cp) => {
  if (!cp?.refCheckpoint) return false;

  const { kind, value, specialType } = cp.refCheckpoint;

  if (kind === 'CommentCheckpoint') return true;
  if (kind === 'BoolCheckpoint' && _.isBoolean(value)) return true;
  if (kind === 'EnumCheckpoint' && _.isString(value)) return true;
  if (kind === 'PhotoCheckpoint') return true;

  return !!(['TextCheckpoint', 'DateCheckpoint', 'NumberCheckpoint'].includes(kind) && (value || specialType?.key));
};

const ValuesSectionElement = React.memo(({ sectionElem, inspectionId, onValueSubmitted }) => {
  const filteredCheckpointsArray = (sectionElem?.checkpoints || []).filter(filterCheckpoints);

  if (!filteredCheckpointsArray.length) return null;

  return (
    <div
      style={{
        backgroundColor: '#EEEEEE',
        padding: 15,
        marginBottom: 20,
        borderRadius: 12,
      }}
    >
      <div
        style={{
          fontSize: 16,
          marginBottom: 10,
          fontWeight: 800,
          color: '#1f5288',
        }}
      >
        {sectionElem.sectionName}
      </div>

      {filteredCheckpointsArray.map((cp) => {
        const checkpoint = cp.refCheckpoint;
        const key = `item-cpDiv-${checkpoint._id}`;

        if (['BoolCheckpoint', 'EnumCheckpoint'].includes(checkpoint.kind)) {
          return (
            <div key={key}>
              <div className='formTitle' style={{ fontSize: 12, marginBottom: 5, marginTop: 5 }}>
                {checkpoint.label}
              </div>

              <SelectBox value={checkpoint.value} inspectionId={inspectionId} sectionId={sectionElem._id} checkpoint={checkpoint} reload={onValueSubmitted} />
            </div>
          );
        }

        if (['CommentCheckpoint', 'TextCheckpoint', 'NumberCheckpoint'].includes(checkpoint.kind)) {
          return (
            <div key={key}>
              <div className='formTitle' style={{ fontSize: 12, marginBottom: 5, marginTop: 5 }}>
                {checkpoint.label}
              </div>

              <CommentBox value={checkpoint.value} inspectionId={inspectionId} sectionId={sectionElem._id} checkpoint={checkpoint} reload={onValueSubmitted} />
            </div>
          );
        }

        if (checkpoint.kind === 'DateCheckpoint') {
          return (
            <div key={key}>
              <div className='formTitle' style={{ fontSize: 12, marginBottom: 5, marginTop: 5 }}>
                {checkpoint.label}
              </div>

              <DateBox value={checkpoint.value} inspectionId={inspectionId} sectionId={sectionElem._id} checkpoint={checkpoint} reload={onValueSubmitted} />
            </div>
          );
        }

        if (checkpoint.kind === 'PhotoCheckpoint') {
          return (
            <div key={key}>
              <div className='formTitle' style={{ fontSize: 12, marginBottom: 5, marginTop: 5 }}>
                {checkpoint.label}
              </div>

              <PhotoBox value={checkpoint?.value || checkpoint?.uri} inspectionId={inspectionId} sectionId={sectionElem._id} checkpoint={checkpoint} reload={onValueSubmitted} />
            </div>
          );
        }

        return null;
      })}
    </div>
  );
});

ValuesSectionElement.propTypes = {
  sectionElem: PropTypes.object.isRequired,
  inspectionId: PropTypes.string,
  onValueSubmitted: PropTypes.func.isRequired,
};

const ReportSectionsList = React.memo(({ sections, inspectionId, onValueSubmitted }) => (
  <div>
    {sections.map((section) => {
      if (!section?.checkpoints?.length) return null;

      return <ValuesSectionElement key={`itemReport-${section._id}`} sectionElem={section} inspectionId={inspectionId} onValueSubmitted={onValueSubmitted} />;
    })}
  </div>
));

ReportSectionsList.propTypes = {
  sections: PropTypes.array.isRequired,
  inspectionId: PropTypes.string,
  onValueSubmitted: PropTypes.func.isRequired,
};

const ModalEditReport = ({ inspectionId, isOpen, onRequestClose, application }) => {
  const [needsPdfRebuild, setNeedsPdfRebuild] = useState(false);
  const [reportSections, setReportSections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const loadInspectionReport = useCallback(
    async ({ withLoader = true } = {}) => {
      if (!isOpen || !inspectionId) return;

      if (withLoader) setIsLoading(true);
      setError('');

      try {
        const result = await InspectionsServices.getInspectionById(inspectionId);

        if (result?.status === 200) {
          setReportSections(_.get(result, 'body.report.sections', []));
        } else {
          setReportSections([]);
          setError(_.get(result, 'body.message', 'Erreur lors du chargement du rapport'));
        }
      } catch (e) {
        setReportSections([]);
        setError(e?.message || 'Erreur lors du chargement du rapport');
      } finally {
        if (withLoader) {
          setIsLoading(false);
        }
      }
    },
    [isOpen, inspectionId],
  );

  useEffect(() => {
    loadInspectionReport({ withLoader: true });
  }, [loadInspectionReport]);

  const handleNewValueSubmitted = useCallback(async () => {
    setNeedsPdfRebuild((prev) => {
      if (prev === true) return prev;
      return true;
    });

    await loadInspectionReport({ withLoader: false });
  }, [loadInspectionReport]);

  const handleCloseModal = useCallback(() => {
    if (needsPdfRebuild === true && inspectionId) {
      if (application?.user?.isAdmin === true) {
        InspectionsServices.fillFromWorkerInspection(inspectionId);
      } else {
        InspectionsServices.documentRegen(inspectionId);
      }
    }

    setNeedsPdfRebuild(false);
    onRequestClose?.({});
  }, [needsPdfRebuild, inspectionId, application?.user?.isAdmin, onRequestClose]);

  return (
    <ModalBody
      closeModal={handleCloseModal}
      title='Edition du rapport'
      isOpen={isOpen}
      actionSave={null}
      isLoading={isLoading}
      updateAction={false}
      error={error}
      loadingButton={null}
      classToAdd='editReport'
    >
      <div className='modalEditReport'>
        <Row>
          <Col xs={12}>
            {reportSections.length > 0 ? (
              <ReportSectionsList sections={reportSections} inspectionId={inspectionId} onValueSubmitted={handleNewValueSubmitted} />
            ) : (
              <p>Aucune valeur</p>
            )}
          </Col>
        </Row>
      </div>
    </ModalBody>
  );
};

ModalEditReport.propTypes = {
  inspectionId: PropTypes.string,
  onRequestClose: PropTypes.func,
  isOpen: PropTypes.bool,
  application: PropTypes.object,
};

const mapStateToProps = (state) => ({
  application: state.application,
});

export default connect(mapStateToProps, null)(ModalEditReport);
